import { Component, OnInit } from '@angular/core';
import * as _ from 'lodash';

import {
  convertPackagesDataToArray, convertPackagesScoreDataToArray,
  getPackagesSegmentDataForType, getAvailableSegmentScoreTypes,
} from 'shared/product/product-helpers';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { packagesSegmentData, extendedInformationData } from '../../../segment-data/packages-segment-data';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { BaseScorecardListComponent } from '../base/_base-scorecard-list.component';


@Component({
  moduleId: module.id,
  selector: 'packages-scorecard',
  templateUrl: 'packages-scorecard.component.html',
  styleUrls: ['packages-scorecard.component.scss'],
})
export class PackagesScorecardComponent extends BaseScorecardListComponent implements OnInit {
  segmentTitle: string = 'Packages';
  segmentName: string = 'packages';
  pathToScorecard = this.segmentName;
  segmentData: any = packagesSegmentData;
  extInfoSegmentData: any = extendedInformationData;

  itemDetailDataArray = []; // itemDetailData object which is converted to array
  packagesScoreDataArray; // scoreData object which is filtered for packages and converted to an array
  extInfoScoreData = {}; // scoreData object which is filtered for packages and converted to an array
  segmentScoreTypes; // list of available score types

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailData(): Array<any> {
    return this.itemDetailDataArray;
  }

  get extInfoItemDetailData(): any {
    return _.get(this.itemDetail, 'extendedInformation');
  }

  get extInfoScorecardComponentTitle(): string {
    return this.getComponentTitle('EXPI');
  }

  // replace parent method
  get segmentScores(): any {
    return this.packagesScoreDataArray;
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    this.updateExtInfoData();
    this.itemDetailDataArray = _.flatten(convertPackagesDataToArray(_.get(this.itemDetail, this.segmentName)));
    this.handleScoreData(this.scoreData);
    super.ngOnInit();
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected getScorecardTitle(item: any) {
    return item.packageUomTitle ? item.packageUomTitle : '';
  }

  protected getSegmentDataForType(type: string) {
    return getPackagesSegmentDataForType(type, this.segmentData);
  }

  protected getScoreItem(item: SegmentFieldItem, index: number|string): any {
    if (!!item && !!this.segmentScores) {
      const segmentScore = this.segmentScores.find((s) => _.get(s, `["packages.${item.item_name}"]`));
      return segmentScore ? _.get(segmentScore, `["packages.${item.item_name}"]`) : null;
    }
    return null;
  }

  /*
    * Updates populated value which affect on status icon
    * $event must have populated: 1|0 property
  */
  protected handleControlPopulated($event?: any): void {
    const score: any = this.getItemFieldScoreFromEvent($event);
    if (score && score.populated !== $event.populated) {
      score.populated = $event.populated;
      this.ref.markForCheck();
    }
  }

  /*
    * Replaced parent
    * $event: {item: SegmentFieldItem, index: number, valueIndex?: number}
   */
  protected handleControlFocus($event: any) {
    if (!_.isNil($event.index)) {
      this.handleNestedControlFocus($event);
    } else {
      this.handleExtInfoControlFocus($event);
    }
  }

  protected handleExtInfoControlFocus(data: any): void {
    const scorecardItem = this.findScorecardItem('fieldMap', data.item.field_map);
    this.collapseExpandedScores(data.item.field_map);
    if (_.get(scorecardItem, 'collapsed')) {
      if (this.scorecard) {
        this.scorecard.scrollToScorecardItem(scorecardItem);
      }
      _.defer(() => {
        scorecardItem.collapseDetails(false);
        this.ref.markForCheck();
      });
    }
  }

  protected findScorecardItem(scorecardProperty: string, value: any): ScorecardItemComponent {
    return this.scorecardItems.find((scorecardItem: ScorecardItemComponent) => {
      return scorecardItem[scorecardProperty] === value;
    });
  }

  /*
    * Replaced parent
    * $event: {status: string, item: SegmentFieldItem, index?: number, valueIndex?: number|null}
   */
  protected getItemFieldScoreFromEvent($event: any) {
    // if there is index - it's packages
    if (!_.isNil($event.index)) {
      return super.getItemFieldScoreFromEvent($event);
    } else {
      // if not - it's extendedInformation
      return _.get(this.extInfoScoreData, $event.item.item_name);
    }
  }

  protected hasScoreForPackageType(type: string) {
    return _.indexOf(this.segmentScoreTypes, type) >= 0;
  }

  // redefine parent method
  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (scorecardField: any) => {
      return (scorecardField.path.indexOf(`${this.segmentName}.${name}`) === 0);
    });
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private handleScoreData(scoreData: any) {
    const segmentScores = _.get(this.scoreData, `segments.${this.segmentName}.tallies`);
    if (!segmentScores) { return; }

    this.packagesScoreDataArray = convertPackagesScoreDataToArray(segmentScores);

    this.segmentScoreTypes = getAvailableSegmentScoreTypes(segmentScores);

    this.ref.markForCheck();
  }

  private updateExtInfoData() {
    this.extInfoScoreData =  _.get(this.scoreData, `segments.extendedInformation.tallies`);

    this.ref.markForCheck();
  }
}
